import React, { useState } from 'react';
import { Form, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ModalPopupComponent from '../ModalPopup/ModalPopup';
import { RootState } from '../../redux/store/store';
import './placeOrder.scss';

interface PlaceOrderProps {
  totalAmount: number;
  onBack: () => void;
}

const PlaceOrder: React.FC<PlaceOrderProps> = ({ totalAmount, onBack }) => {
  const navigate = useNavigate();
  const userData = useSelector((state: RootState) => state.login);
  const { userName } = userData;

  const [formData, setFormData] = useState({
    shippingAddress: '',
    expectedDeliveryDate: '',
    paymentMethod: ''
  });

  const [errors, setErrors] = useState({
    shippingAddress: '',
    expectedDeliveryDate: '',
    paymentMethod: ''
  });

  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    // Clear error when user starts typing
    if (errors[name as keyof typeof errors]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const validateForm = () => {
    let isValid = true;
    const newErrors = {
      shippingAddress: '',
      expectedDeliveryDate: '',
      paymentMethod: ''
    };

    if (!formData.shippingAddress.trim()) {
      newErrors.shippingAddress = 'Shipping address is required';
      isValid = false;
    }

    if (!formData.expectedDeliveryDate) {
      newErrors.expectedDeliveryDate = 'Expected delivery date is required';
      isValid = false;
    }

    if (!formData.paymentMethod) {
      newErrors.paymentMethod = 'Payment method is required';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handlePlaceOrder = () => {
    if (validateForm()) {
      // Handle place order logic here
      const orderData = {
        userName,
        shippingAddress: formData.shippingAddress,
        expectedDeliveryDate: formData.expectedDeliveryDate,
        totalAmount,
        paymentMethod: formData.paymentMethod
      };
      
      console.log('Order placed:', orderData);
      
      // Show success modal
      setShowSuccessModal(true);
    }
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    navigate('/dashboard');
  };

  return (
    <div className="place-order-container">
      <div className="place-order-header">
        <h1>Place Order</h1>
      </div>

      <div className="place-order-content">
        <div className="order-form-card">
          <h2>Order Details</h2>
          
          <Form>
            {/* User Name - Read Only */}
            <Form.Group className="mb-4">
              <Form.Label>User Name</Form.Label>
              <Form.Control
                type="text"
                value={userName || ''}
                readOnly
                disabled
                className="readonly-field"
              />
            </Form.Group>

            {/* Shipping Address */}
            <Form.Group className="mb-4">
              <Form.Label>Shipping Address <span className="required">*</span></Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="shippingAddress"
                value={formData.shippingAddress}
                onChange={handleInputChange}
                placeholder="Enter your shipping address"
                isInvalid={!!errors.shippingAddress}
              />
              <Form.Control.Feedback type="invalid">
                {errors.shippingAddress}
              </Form.Control.Feedback>
            </Form.Group>

            {/* Expected Date for Delivery */}
            <Form.Group className="mb-4">
              <Form.Label>Expected Date for Delivery <span className="required">*</span></Form.Label>
              <Form.Control
                type="date"
                name="expectedDeliveryDate"
                value={formData.expectedDeliveryDate}
                onChange={handleInputChange}
                min={new Date(new Date().setDate(new Date().getDate() + 1)).toISOString().split('T')[0]}
                isInvalid={!!errors.expectedDeliveryDate}
              />
              <Form.Control.Feedback type="invalid">
                {errors.expectedDeliveryDate}
              </Form.Control.Feedback>
            </Form.Group>

            {/* Total Amount - Read Only */}
            <Form.Group className="mb-4">
              <Form.Label>Total Amount</Form.Label>
              <Form.Control
                type="text"
                value={`$${totalAmount.toFixed(2)}`}
                readOnly
                disabled
                className="readonly-field amount-field"
              />
            </Form.Group>

            {/* Payment Method */}
            <Form.Group className="mb-4">
              <Form.Label>Payment Method <span className="required">*</span></Form.Label>
              <Form.Select
                name="paymentMethod"
                value={formData.paymentMethod}
                onChange={handleInputChange}
                isInvalid={!!errors.paymentMethod}
              >
                <option value="">Select Payment Method</option>
                <option value="credit_card">Credit Card</option>
                <option value="debit_card">Debit Card</option>
                <option value="paypal">PayPal</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="purchase_order">Purchase Order</option>
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {errors.paymentMethod}
              </Form.Control.Feedback>
            </Form.Group>
          </Form>

          {/* Action Buttons */}
          <div className="order-action-buttons">
            <Button 
              variant="secondary" 
              className="btn-cancel"
              onClick={onBack}
            >
              Back
            </Button>
            <Button 
              variant="primary" 
              className="btn-place-order"
              onClick={handlePlaceOrder}
            >
              Place Order
            </Button>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      <ModalPopupComponent
        show={showSuccessModal}
        onHide={handleSuccessModalClose}
        title=""
        footerButtons={[
          {
            label: "Ok",
            variant: "primary",
            onClick: handleSuccessModalClose
          }
        ]}
      >
        <div style={{ textAlign: 'center'}}>
          <CheckCircleIcon className='successTick' />
          <h3 style={{ color: '#1F2937', fontWeight: '600', marginBottom: '10px' }}>Your order placed successfully</h3>
        </div>
      </ModalPopupComponent>
    </div>
  );
};

export default PlaceOrder;
